import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClockIcon, PlayIcon, CubeIcon } from '@heroicons/react/24/outline';

interface Experiment {
  title: string;
  description: string;
  route: string;
  kind?: 'reorderable-list' | 'unity-game' | 'photo-gallery' | string;
}

interface ExperimentCardProps {
  experiment: Experiment;
}

const ExperimentCard: FunctionComponent<ExperimentCardProps> = ({ experiment }) => {
  const navigate = useNavigate();
  const setRoute = (route: string) => {
    navigate(route);
  };

  const kind = experiment.kind;

  return (
    <div
      className="group relative flex flex-col overflow-hidden rounded-xl border border-white/10 bg-gradient-to-b from-white/5 to-white/0 p-3 backdrop-blur-sm transition hover:border-primary/40 hover:from-white/10 hover:shadow-glow cursor-pointer"
      onClick={() => setRoute(experiment.route)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setRoute(experiment.route);
        }
      }}
    >
      <div className="aspect-video w-full overflow-hidden rounded-lg ring-1 ring-white/10 relative bg-gradient-to-br from-primary/25 to-indigo-500/20 flex items-center justify-center">
        {kind === 'reorderable-list' ? (
          <div className="relative w-[85%] h-[70%] flex flex-col gap-2">
            {[0,1,2,3].map(i => (
              <div
                key={i}
                className="relative h-6 rounded-md bg-white/7 ring-1 ring-white/15 backdrop-blur-sm flex items-center px-3 text-[11px] text-white/70 tracking-wide font-medium transition-transform duration-500 will-change-transform"
                style={{
                  animation: 'var(--card-hover)'
                }}
              >
                <span className="h-2 w-2 rounded-sm mr-2 bg-gradient-to-br from-primary/70 to-cyan-400/70 shadow-glow" />
                Item {i+1}
                <span className="absolute inset-0 rounded-md opacity-0 group-hover:opacity-100 transition bg-[linear-gradient(110deg,rgba(255,255,255,0.08),transparent_40%,rgba(255,255,255,0.06)_60%,transparent_75%)] [background-size:200%_100%] group-hover:animate-[badgeShimmer_1.4s_linear]" />
              </div>
            ))}
            {/* Hover reorder simulation overlay */}
            <div className="pointer-events-none absolute inset-0 group-hover:[--card-hover:reorderSim_2s_ease-in-out_infinite]" />
          </div>
        ) : kind === 'unity-game' ? (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="relative">
              <CubeIcon className="h-16 w-16 text-purple-400/80 group-hover:scale-110 transition-transform duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-400/20 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500" />
              <PlayIcon className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-6 w-6 text-white/90 group-hover:scale-110 transition-transform duration-300" />
            </div>
            <div className="absolute bottom-2 right-2 text-[10px] text-purple-300/80 font-medium tracking-wide">UNITY</div>
          </div>
        ) : kind === 'photo-gallery' ? (
          <div className="flex h-full w-full items-start gap-1.5 p-3">
            {[
              [40, 26, 32],
              [26, 38, 30],
              [34, 24, 36]
            ].map((column, ci) => (
              <div key={ci} className="flex flex-1 flex-col gap-1.5">
                {column.map((h, i) => (
                  <div
                    key={i}
                    className="rounded-sm bg-gradient-to-br from-white/25 to-white/5 ring-1 ring-white/15 transition-transform duration-500 group-hover:translate-y-[-2px]"
                    style={{ height: `${h}%`, transitionDelay: `${(ci * 3 + i) * 35}ms` }}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-white/40">Preview</div>
        )}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.15),transparent_60%)]" />
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <h3 className="text-base font-semibold leading-snug tracking-tight text-white line-clamp-2">
          {experiment.title}
        </h3>
        <div className="flex items-center gap-1.5 text-xs text-white/60">
          <ClockIcon className="h-4 w-4" />
          <span>5 hours</span>
        </div>
        <p className="text-sm text-white/70 line-clamp-3">
          {experiment.description}
        </p>
      </div>
      <div className="pointer-events-none absolute inset-px rounded-[11px] opacity-0 ring-1 ring-primary/40 transition group-hover:opacity-100" />
    </div>
  );
};

export default ExperimentCard;

/* Keyframes injected via global CSS: we rely on existing badgeShimmer; need reorderSim for subtle vertical shuffle */
